import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { UserWithRole } from './types';

export interface CreateUserData {
  email: string;
  name: string;
  role: UserWithRole['role'];
  password?: string;
}

export interface ResetPasswordData {
  userId: string;
  newPassword: string;
}

export const createUserByAdmin = async (userData: CreateUserData): Promise<boolean> => {
  try {
    console.log('Creating user:', userData);

    // Validate input
    if (!userData.email || !userData.name || !userData.role) {
      toast.error('Missing required fields');
      return false;
    }

    // Get current session to ensure we're authenticated
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      toast.error('Authentication required');
      return false;
    }

    // Use the invite-user edge function to create user with role
    const { data, error } = await supabase.functions.invoke('invite-user', {
      body: {
        email: userData.email.trim(),
        name: userData.name.trim(),
        role: userData.role
      }
    });

    console.log('Edge function response:', { data, error });

    if (error) {
      console.error('Error creating user via edge function:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to create user';
      if (error.message) {
        if (error.message.includes('already exists')) {
          errorMessage = 'A user with this email already exists';
        } else if (error.message.includes('permissions')) {
          errorMessage = 'Insufficient permissions to create user';
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
      return false;
    }

    // Check if the edge function returned success
    if (data?.success) {
      toast.success(data.message || 'User created successfully');
      console.log('User created successfully:', data.user);
      return true;
    } else {
      const errorMsg = data?.error || data?.message || 'Failed to create user';
      console.error('Edge function returned error:', data);
      toast.error(errorMsg);
      return false;
    }
  } catch (error) {
    console.error('Error creating user:', error);
    
    // Handle network errors
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        toast.error('Network error - please check your connection');
      } else if (error.message.includes('timeout')) {
        toast.error('Request timed out - please try again');
      } else {
        toast.error('Failed to create user: ' + error.message);
      }
    } else {
      toast.error('An unexpected error occurred');
    }
    
    return false;
  }
};

export const resetUserPassword = async (resetData: ResetPasswordData): Promise<boolean> => {
  try {
    // Get current session to ensure we're authenticated
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      toast.error('Authentication required');
      return false;
    }

    console.log('Calling reset-user-password edge function for user:', resetData.userId);

    // Call the edge function which has admin privileges
    const { data, error } = await supabase.functions.invoke('reset-user-password', {
      body: {
        userId: resetData.userId,
        newPassword: resetData.newPassword
      }
    });

    if (error) {
      console.error('Error calling reset-user-password function:', error);
      let errorMessage = 'Failed to reset password';
      if (error.message) {
        if (error.message.includes('permissions')) {
          errorMessage = 'Insufficient permissions to reset password';
        } else if (error.message.includes('not found')) {
          errorMessage = 'User not found';
        } else {
          errorMessage = error.message;
        }
      }
      toast.error(errorMessage);
      return false;
    }

    // Check if the edge function returned success
    if (data?.success) {
      console.log('Password reset successfully:', data);
      toast.success(data.message || 'Password reset successfully');
      return true;
    } else {
      const errorMsg = data?.error || 'Failed to reset password';
      console.error('Reset password function returned error:', data);
      toast.error(errorMsg);
      return false;
    }
  } catch (error) {
    console.error('Error resetting password:', error);
    if (error instanceof Error) {
      toast.error('Failed to reset password: ' + error.message);
    } else {
      toast.error('Failed to reset password');
    }
    return false;
  }
};

export const bulkCreateUsers = async (usersData: CreateUserData[]): Promise<{success: number, failed: number}> => {
  let successCount = 0;
  let failedCount = 0;

  // Process users in batches to avoid overwhelming the server
  const batchSize = 5;
  for (let i = 0; i < usersData.length; i += batchSize) {
    const batch = usersData.slice(i, i + batchSize);
    
    // Process batch in parallel
    const results = await Promise.allSettled(
      batch.map(userData => createUserByAdmin(userData))
    );

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        successCount++;
      } else {
        failedCount++;
        console.error(`Failed to create user ${batch[index].email}:`, 
          result.status === 'rejected' ? result.reason : 'Unknown error');
      }
    });

    // Add a small delay between batches to avoid rate limiting
    if (i + batchSize < usersData.length) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }

  if (successCount > 0) {
    toast.success(`Successfully created ${successCount} users`);
  }
  if (failedCount > 0) {
    toast.error(`Failed to create ${failedCount} users`);
  }

  return { success: successCount, failed: failedCount };
};

export const getUserProfile = async (userId: string): Promise<UserWithRole | null> => {
  try {
    // Get current session to ensure we're authenticated
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error('Authentication required');
      return null;
    }

    // Use admin-safe function to get user profile if it exists
    let profile = null;
    
    try {
      const { data, error } = await supabase.rpc('admin_get_user_profile', {
        target_user_id: userId
      });
      
      if (!error && data && data.length > 0) {
        profile = data[0];
      }
    } catch (rpcError) {
      console.warn('admin_get_user_profile function not available, falling back to direct query');
    }

    // Fallback to direct profile query if RPC function doesn't exist
    if (!profile) {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
      profile = data;
    }

    if (!profile) {
      return null;
    }
    
    // Get user role
    let roleData = 'user';
    try {
      const { data } = await supabase.rpc('admin_get_user_role', {
        target_user_id: userId
      });
      if (data) roleData = data;
    } catch (rpcError) {
      // Fallback to direct role query
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();
      if (data) roleData = data.role;
    }

    return {
      id: profile.id,
      name: profile.name,
      email: profile.email,
      is_approved: profile.is_approved,
      created_at: profile.created_at,
      avatar_url: profile.avatar_url,
      role: (roleData || 'user') as UserWithRole['role']
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

// Helper function to validate user data
export const validateUserData = (userData: CreateUserData): string[] => {
  const errors: string[] = [];

  if (!userData.email) {
    errors.push('Email is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
    errors.push('Invalid email format');
  }

  if (!userData.name || userData.name.trim().length < 2) {
    errors.push('Name must be at least 2 characters long');
  }

  if (!userData.role || !['user', 'admin', 'staff'].includes(userData.role)) {
    errors.push('Valid role is required');
  }

  return errors;
};